import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchBeliefs, fetchSchema, fetchStories } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import {
  FilterBar,
  SearchInput,
  FilterSelect,
  MultiSelectChips,
} from "@/components/FilterBar";
import { Tag } from "@/components/Tag";
import { BeliefEditor } from "@/components/BeliefEditor";
import { Plus, ArrowRight, Pencil } from "lucide-react";
import { fmtDate } from "@/lib/format";

export default function BeliefsPage() {
  const [beliefs, setBeliefs] = useState([]);
  const [stories, setStories] = useState([]);
  const [schema, setSchema] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [offerTags, setOfferTags] = useState([]);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [b, s, sc] = await Promise.all([
      fetchBeliefs(),
      fetchStories(),
      fetchSchema(),
    ]);
    setBeliefs(b);
    setStories(s);
    setSchema(sc);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("indielaw:sync-complete", handler);
    return () => window.removeEventListener("indielaw:sync-complete", handler);
  }, [load]);

  const storyCountByBelief = useMemo(() => {
    const m = {};
    stories.forEach((st) => {
      (st.linked_belief_ids || []).forEach((id) => {
        m[id] = (m[id] || 0) + 1;
      });
    });
    return m;
  }, [stories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return beliefs.filter((b) => {
      if (q && !b.belief_text.toLowerCase().includes(q)) return false;
      if (category && b.category !== category) return false;
      if (status && b.status !== status) return false;
      if (offerTags.length > 0) {
        const tags = b.offer_tags || [];
        const hasAll = offerTags.every((t) => tags.includes(t));
        if (!hasAll) return false;
      }
      return true;
    });
  }, [beliefs, query, category, status, offerTags]);

  const toggleOfferTag = (t) =>
    setOfferTags((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );

  return (
    <div data-testid="beliefs-page">
      <PageHeader
        eyebrow="Table 1"
        title="Beliefs"
        subtitle="The atomic units of your messaging. Filter, search, and edit the things you stand for."
        testId="beliefs-page-header"
        actions={
          <button
            data-testid="new-belief-button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 bg-[#141B4D] text-[#F2E9DB] px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#141B4D]/90"
          >
            <Plus size={14} strokeWidth={2.5} />
            New belief
          </button>
        }
      />

      <FilterBar>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search belief text…"
          testId="beliefs-search-input"
        />
        <FilterSelect
          value={category}
          onChange={setCategory}
          options={schema?.belief_categories || []}
          placeholder="All categories"
          testId="beliefs-category-filter"
        />
        <FilterSelect
          value={status}
          onChange={setStatus}
          options={schema?.belief_statuses || []}
          placeholder="Any status"
          testId="beliefs-status-filter"
        />
      </FilterBar>

      <div className="mb-8" data-testid="beliefs-offer-tags-filter">
        <div className="text-xs tracking-[0.05em] uppercase text-[#141B4D]/60 mb-2">
          Offer tags
        </div>
        <MultiSelectChips
          options={schema?.offer_tags || []}
          selected={offerTags}
          onToggle={toggleOfferTag}
          testId="beliefs-offer-tags-chips"
        />
      </div>

      <div className="mb-3 text-xs text-[#141B4D]/60" data-testid="beliefs-count">
        {loading ? "Loading…" : `${filtered.length} of ${beliefs.length} beliefs`}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-[#141B4D]">
              <th className="py-3 pr-4 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[44%]">
                Belief
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Category
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Offer Tags
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Status
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Stories
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Added
              </th>
              <th className="py-3 pl-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[1%]"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr
                key={b.id}
                data-testid={`belief-row-${b.id}`}
                className="border-b border-[#141B4D]/15 hover:bg-[#141B4D]/5 transition-colors"
              >
                <td className="py-4 pr-4 align-top">
                  <Link
                    to={`/beliefs/${b.id}`}
                    data-testid={`belief-link-${b.id}`}
                    className="text-sm text-[#141B4D] font-medium leading-relaxed hover:underline decoration-[#FFD100] decoration-2 underline-offset-4"
                  >
                    {b.belief_text}
                  </Link>
                  {b.source && (
                    <div className="text-xs text-[#141B4D]/50 mt-1.5">
                      {b.source}
                    </div>
                  )}
                </td>
                <td className="py-4 px-3 align-top text-xs text-[#141B4D]/80">
                  {b.category || "—"}
                </td>
                <td className="py-4 px-3 align-top">
                  <div className="flex flex-wrap gap-1.5">
                    {(b.offer_tags || []).map((t) => (
                      <Tag key={t}>{t}</Tag>
                    ))}
                  </div>
                </td>
                <td className="py-4 px-3 align-top">
                  <Tag variant={b.status === "Active" ? "active" : "retired"}>
                    {b.status}
                  </Tag>
                </td>
                <td className="py-4 px-3 align-top text-sm text-[#141B4D]">
                  <span
                    className={
                      (storyCountByBelief[b.id] || 0) === 0
                        ? "text-rose-600 font-semibold"
                        : ""
                    }
                  >
                    {storyCountByBelief[b.id] || 0}
                  </span>
                </td>
                <td className="py-4 px-3 align-top text-xs text-[#141B4D]/60 whitespace-nowrap">
                  {fmtDate(b.date_added)}
                </td>
                <td className="py-4 pl-3 align-top whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditing(b)}
                      data-testid={`belief-edit-${b.id}`}
                      className="p-1.5 text-[#141B4D]/60 hover:text-[#141B4D] hover:bg-[#141B4D]/5 rounded-sm"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <Link
                      to={`/beliefs/${b.id}`}
                      data-testid={`belief-open-${b.id}`}
                      className="p-1.5 text-[#141B4D]/60 hover:text-[#141B4D] hover:bg-[#141B4D]/5 rounded-sm"
                      title="Open"
                    >
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-[#141B4D]/50">
                  No beliefs match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <BeliefEditor
        open={creating || !!editing}
        belief={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => load()}
      />
    </div>
  );
}
